import { DeviceInfo } from '@capacitor/device';
import Application from 'Application';
import { useDevice } from 'hooks/useDevice';
import React, { useEffect } from 'react';
import { FallbackProps, withErrorBoundary } from "react-error-boundary";
import { Provider, useDispatch } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { setDevice } from 'service/app-config/actions';
import IAppProps from './IAppProps';
import GlobalError from './components/error/global-error/GlobalError';
import RootContainer from './container/RootContainer';
import store from './service/store';
import Utils from './utils/Utils';

const App: React.FC<IAppProps> = ({ device }: { device: DeviceInfo | null }) => {
    const dispatch = useDispatch();

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                window.location.reload();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    React.useEffect(() => {
        const start = async (): Promise<void> => {
            const appTitle = String(Application.getInstance().title);
            const manifestDir = String(Application.getInstance().manifestPath);
            Utils.setAttributes({ appTitle, directory: manifestDir });
            await Utils.getForexRates();
        };
        start();
    }, []);

    React.useEffect(() => {
        if (device) {
            dispatch(setDevice(device));
        }
    }, [device, dispatch]);

    return (
        <BrowserRouter>
            <RootContainer />
        </BrowserRouter>
    );
};

const withProviders = (Component: React.FC<{ device: DeviceInfo | null }>) => {
    const MemoizedApplication = React.memo(Component);
    const Wrapper: React.FC = () => {
        const operatingDevice = useDevice();

        return (
            <Provider store={store}>
                <MemoizedApplication device={operatingDevice} />
            </Provider>
        );
    };

    return React.memo(Wrapper);
};

export default withProviders(withErrorBoundary(App, {
    FallbackComponent: (_: FallbackProps) => <GlobalError />,
    onError: (error: Error) => console.error(error)
}));
